(ns practitest.frontend.components2.dashboard.charts.render
  (:require ["d3" :as d3]
            [malli.core :as m]
            [practitest.frontend.components2.dashboard.charts.common :as c]
            [practitest.frontend.components2.dashboard.charts.scales :as scales]
            [practitest.frontend.components2.dashboard.charts.marks :as marks]
            [practitest.frontend.components2.dashboard.charts.axis :as axis]
            [practitest.frontend.components2.dashboard.charts.tooltip :as tooltip]))

(def defaults
  {:vector-effect "non-scaling-stroke"})

(def cartesian-defaults
  {:width   800
   :height  400
   :padding {:top 5 :right 20 :bottom 50 :left 25}
   :encode  {:x {:axis {:orient :bottom}}
             :y {:axis {:orient :left}}}})

(defn- infer-coord-system [{{:keys [x y theta radius]} :encode}]
  (cond
    (or theta radius) :polar
    (or x y)          :cartesian))

(defn- prepare-marks [{:keys [mark marks] :as spec}]
  (cond-> spec
    (and (not marks) mark)
    (->
      (assoc :marks [(if (keyword? mark) {:type mark} mark)])
      (dissoc :mark))))

(defn- build-cartesian-ctx [spec]
  (-> (c/deep-merge cartesian-defaults spec)
      scales/with-scales))

(defn setup-svg!
  "Apply svg-level attrs: viewBox sized to outer dims, plus a <style> block
   that applies :vector-effect to all descendant paths."
  [svg-el {:keys [padding width height vector-effect]}]
  (let [vb-w (+ width (or (:left padding) 0) (or (:right padding) 0))
        vb-h (+ height (or (:top padding) 0) (or (:bottom padding) 0))
        svg  (-> (.select d3 svg-el)
                 (.attr "viewBox" (str "0 0 " vb-w " " vb-h)))]
    (when vector-effect
      (c/ensure-style! svg (str "path { vector-effect: " vector-effect "; }")))
    svg))

(defn cartesian-layout!
  "Ensure chart layout groups exist on the svg and position them.
   Layout:
     .chart       — translated by (padding.left, padding.top)
       .y-axis    — at (0, 0)
       .x-axis    — at (0, height)
       .marks     — at (0, 0), data marks go here
       .legend    — at (0, height + encode.color.legend.offset)"
  [svg {:keys [padding height encode]}]
  (let [legend-offset (or (get-in encode [:color :legend :offset]) 20)
        g-chart       (-> (c/ensure-group! svg "chart")
                          (c/translate! (:left padding) (:top padding)))]
    (c/ensure-group! g-chart "y-axis")
    (-> (c/ensure-group! g-chart "x-axis")
        (c/translate! 0 height))
    (c/ensure-group! g-chart "marks")
    (-> (c/ensure-group! g-chart "legend")
        (c/translate! 0 (+ height legend-offset)))
    (tooltip/setup-tooltip! g-chart)
    svg))

(defn- normalize-padding [p]
  (cond
    (number? p) {:top p :right p :bottom p :left p}
    (map? p)    p))

(defn- normalize-scales [spec]
  (update spec :encode update-vals
          (fn [{:keys [scale] :as channel}]
            (cond-> channel
              (vector? scale) (assoc :scale {:domain scale})))))

(defn- lift-color-palette [{:keys [color-palette] :as spec}]
  (cond-> spec
    color-palette
    (update-in [:encode :color :scale]
               (fn [s]
                 (-> s
                     (update :domain #(or % (vec (keys color-palette))))
                     (update :range  #(or % (vec (vals color-palette)))))))))

(defn- build-context [spec]
  (let [spec         (-> (prepare-marks spec)
                         normalize-scales
                         lift-color-palette
                         (update :padding normalize-padding))
        coord-system (infer-coord-system spec)
        cartesian?   (= coord-system :cartesian)]
    (-> (c/deep-merge defaults spec)
        (assoc :coord-system coord-system :cartesian? cartesian?)
        (cond-> cartesian? build-cartesian-ctx))))

(defn- element-inner-width
  "Read the SVG element's current rendered width and subtract padding,
   giving the inner coordinate width for scale ranges."
  [svg-el padding]
  (let [w (some-> svg-el .getBoundingClientRect .-width)]
    (when (pos? w)
      (- w (or (:left padding) 0) (or (:right padding) 0)))))

(defn- render-legend!
  [svg ctx]
  (let [legend-cfg      (get-in ctx [:encode :color :legend])
        items           (:items legend-cfg)
        color-scale  (get-in ctx [:encode :color :d3-scale])
        n               (count items)]
    (when (pos? n)
      (let [inner-width     (:width ctx)
            bar-height      12
            bar-gap         2
            label-gap       18
            update-duration 350
            on-click        (:on-legend-click ctx)
            item-width      (/ inner-width n)
            bar-width       (- item-width bar-gap)
            legend-g        (.select svg "g.chart g.legend")
            lbl-key         (fn [^js d] (.-label d))
            color-fn        (fn [^js d] (color-scale (.-label d)))
            active-fn       (fn [^js d] (aget d "is-active"))
            class-fn        (fn [^js d] (str "legend-item" (when (active-fn d) " active")))
            sel             (-> legend-g
                                (.selectAll "g.legend-item")
                                (.data (clj->js items) lbl-key))
            entered         (-> (.enter sel)
                                (.append "g")
                                (.attr "class" class-fn)
                                (.attr "transform" (fn [_ i] (str "translate(" (* i item-width) ",0)")))
                                (.each (fn [^js d]
                                         (this-as this
                                           (let [g (.select d3 this)]
                                             (-> g
                                                 (.append "rect")
                                                 (.attr "x" 0)
                                                 (.attr "y" 0)
                                                 (.attr "width" bar-width)
                                                 (.attr "height" bar-height)
                                                 (.attr "fill" color-fn))
                                             (-> g
                                                 (.append "foreignObject")
                                                 (.attr "x" 0)
                                                 (.attr "y" bar-height)
                                                 (.attr "width" bar-width)
                                                 (.attr "height" label-gap)
                                                 (.append "xhtml:div")
                                                 (.attr "class" "legend-label")
                                                 (.text (.-label d))))))))]

        ;; EXIT: fade out removed items
        (-> (.exit sel)
            (.transition)
            (.duration update-duration)
            (.style "opacity" 0)
            (.remove))

        ;; UPDATE: sync active class, position, width, text, fill
        (-> sel
            (.attr "class" class-fn)
            (.transition)
            (.duration update-duration)
            (.attr "transform" (fn [_ i] (str "translate(" (* i item-width) ",0)"))))
        (-> sel (.select "rect") (.attr "fill" color-fn))
        (-> sel (.select "foreignObject") (.attr "width" bar-width))
        (-> sel (.select ".legend-label") (.text (fn [^js d] (.-label d))))

        ;; Wire click on full merged selection (enter + update)
        (let [all (.merge entered sel)]
          (when on-click
            (.on all "click" (fn [^js _event ^js d]
                               (on-click (.-label d))))))))))

(defn render-chart! [svg-el spec]
  (let [padding (normalize-padding (or (:padding spec) (:padding cartesian-defaults)))
        spec    (if-let [w (element-inner-width svg-el padding)]
                  (assoc spec :width w)
                  spec)
        ctx (build-context spec)
        svg (setup-svg! svg-el ctx)]
    (when (:cartesian? ctx)
      (cartesian-layout! svg ctx)
      (axis/render! svg ctx)
      (let [ctx (if (:tooltip spec)
                  (let [tooltip-g (.node (.select svg "g.chart g.chart-tooltip"))]
                    (assoc ctx :tooltip (tooltip/make-tooltip-api tooltip-g (:width ctx))))
                  ctx)]
        (doseq [mark (:marks ctx)]
          (marks/render! svg mark ctx))
        (when (get-in ctx [:encode :color :legend])
          (render-legend! svg ctx))))
    ctx))
